// webPush — the browser half of closed-tab notifications.
//
// Standard Web Push (RFC 8030 + VAPID): register public/sw.js, ask the browser
// for a PushSubscription against the site's VAPID public key, and store that
// subscription in `web_push_subscriptions` (migration 0066) so
// netlify/functions/_web-push.js can deliver to this browser when every tab is
// closed. There is NO Firebase here and none is coming back (Umer,
// 2026-09-16) — the endpoint the browser hands us may happen to live on
// Google's infrastructure, but it is plain RFC 8030 and needs no project,
// no SDK and no credential of ours.
//
// Everything in this file is best-effort and silent. A console whose browser
// refuses push (Safari without a home-screen install, a locked-down profile, a
// site with no VAPID key configured) must behave exactly as it does today: the
// in-tab listeners still alert, nothing throws, nothing is logged at the user.

import { supabase } from './supabaseClient';
import { publicKeyForUrl } from './webPushKeys';

const SW_URL = '/sw.js';
const TABLE = 'web_push_subscriptions';

/** The site's VAPID public key: the in-code table (services/webPushKeys.ts)
 *  keyed by the Supabase project this build talks to, with the env var kept
 *  only as an override for a one-off test site.
 *
 *  Env is read INLINE on every call, never hoisted: Vite replaces the literal
 *  `import.meta.env.…` expression at build time and vitest's stubEnv only
 *  reaches it while it stays written out like this (services/features.ts). */
export function webPushPublicKey(): string {
  const override = String(import.meta.env.VITE_WEB_PUSH_PUBLIC_KEY || '');
  if (override) return override;
  return publicKeyForUrl(String(import.meta.env.VITE_SUPABASE_URL || ''));
}

/** Does this browser have the three pieces the flow needs? */
export function webPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.serviceWorker &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** base64url (what VAPID keys are printed as) → the Uint8Array
 *  `applicationServerKey` wants. Chrome accepts the string form, Firefox and
 *  Safari historically did not, so convert and be done with it. */
export function vapidKeyToBytes(base64: string): Uint8Array {
  const padded = `${base64}${'='.repeat((4 - (base64.length % 4)) % 4)}`
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = atob(padded);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

// One attempt per user per page life. The provider re-checks on every sign-in
// and on every permission change, and re-subscribing is idempotent for the
// browser (same endpoint back) — but it still costs a round trip, so this keeps
// a long-lived console from re-upserting on every render.
let attemptedFor: string | null = null;

/** Test/sign-out hook — module state on purpose (one memo per page load). */
export function __resetWebPushMemo(): void {
  attemptedFor = null;
}

/**
 * Make sure THIS browser has a push subscription stored against `userId`.
 * Returns the endpoint on success, null when the flow was skipped or failed.
 *
 * Preconditions, all of which simply skip:
 *   • permission actually granted (never prompts here — the bell does that);
 *   • a VAPID public key in the build (unset on a site that has not been
 *     configured yet, and unset under vitest, which is what keeps the test run
 *     from registering a worker);
 *   • the browser supports service workers + PushManager.
 */
export async function ensureWebPushSubscription(userId: string): Promise<string | null> {
  if (!userId) return null;
  if (attemptedFor === userId) return null;
  if (!webPushSupported()) return null;
  if (Notification.permission !== 'granted') return null;
  const key = webPushPublicKey();
  if (!key) return null;
  attemptedFor = userId;

  try {
    const reg = await navigator.serviceWorker.register(SW_URL);
    // `register` resolves as soon as the registration exists; `ready` waits for
    // an ACTIVE worker, which pushManager.subscribe needs.
    const ready = (await navigator.serviceWorker.ready) || reg;
    const manager = (ready && ready.pushManager) || (reg && reg.pushManager);
    if (!manager) return null;

    // An existing subscription comes back unchanged (same endpoint), so the
    // upsert below refreshes last_seen_at instead of creating a second row.
    const sub =
      (await manager.getSubscription()) ||
      (await manager.subscribe({
        userVisibleOnly: true, // required by Chrome; we always show something
        applicationServerKey: vapidKeyToBytes(key),
      }));
    if (!sub) return null;

    const json: any = typeof sub.toJSON === 'function' ? sub.toJSON() : sub;
    const p256dh = json?.keys?.p256dh;
    const auth = json?.keys?.auth;
    if (!sub.endpoint || !p256dh || !auth) return null;

    const { error } = await supabase()
      .from(TABLE)
      .upsert(
        {
          endpoint: sub.endpoint,
          user_id: userId,
          p256dh,
          auth,
          user_agent: (navigator.userAgent || '').slice(0, 400),
          last_seen_at: new Date().toISOString(),
        },
        { onConflict: 'endpoint' },
      );
    if (error) throw new Error(error.message);
    return sub.endpoint;
  } catch (e) {
    // Never surfaced: a browser that will not subscribe still has every in-tab
    // alert, and a banner about it would be noise on machines that can never
    // fix it (Safari, locked-down profiles).
    console.warn('web push: subscribe failed:', (e as Error)?.message || e);
    attemptedFor = null; // let the next sign-in try again
    return null;
  }
}

/**
 * Sign-out: stop this browser receiving pushes for the account that is leaving.
 *
 * Two belts, because the order of teardown is not ours to control. The row
 * delete needs a live Supabase session (RLS: `user_id = app_uid()`), and by the
 * time a sign-out has propagated there may not be one — so the subscription is
 * ALSO cancelled at the browser. A cancelled endpoint answers 410 on the next
 * send and _web-push.js prunes the row then, which makes the delete an
 * optimisation rather than a correctness requirement.
 */
export async function dropWebPushSubscription(): Promise<void> {
  attemptedFor = null;
  if (!webPushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    const sub = reg && reg.pushManager ? await reg.pushManager.getSubscription() : null;
    if (!sub) return;
    const { endpoint } = sub;
    try {
      await sub.unsubscribe();
    } catch { /* already gone */ }
    if (endpoint) {
      await supabase().from(TABLE).delete().eq('endpoint', endpoint);
    }
  } catch (e) {
    console.warn('web push: unsubscribe failed:', (e as Error)?.message || e);
  }
}
